import { apiClient } from "./apiClient";
import { FullSearchResult, QuickSearchResult } from "../models/SearchModel";

/**
 * Calls the quick/typeahead search endpoint.
 * Returns up to search.result_limit (admin-configurable) results per category.
 * Used only by the header typeahead dropdown — NOT by the search results page.
 *
 * Uses apiClient instead of raw fetch so that:
 *   - The Authorization header is always sent (required in some nginx configs)
 *   - 401 responses trigger the global logout handler
 *   - Actual backend error messages are surfaced instead of a generic string
 */
export const quickSearch = async (q: string): Promise<QuickSearchResult> => {
  const params = new URLSearchParams({ q });
  return apiClient.get<QuickSearchResult>(`/api/search/quick?${params}`);
};

/**
 * Optional advanced search filters — applied to the entries category only.
 * Dates are ISO yyyy-MM-dd strings straight from the <input type="date"> fields.
 */
export interface SearchFilters {
  author?: string;
  from?: string;
  to?: string;
}

// Appends non-empty filter values to the query string
const appendFilters = (params: URLSearchParams, filters?: SearchFilters) => {
  if (!filters) return;
  if (filters.author) params.set("author", filters.author);
  if (filters.from) params.set("from", filters.from);
  if (filters.to) params.set("to", filters.to);
};

/**
 * Full paginated search for the search results page.
 * Returns the first page of all 6 categories with hasMore + totalElements per category.
 * q may be empty when advanced filters are set (filter-only search).
 */
export const fullSearch = async (
  q: string,
  page = 0,
  size = 20,
  filters?: SearchFilters
): Promise<FullSearchResult> => {
  const params = new URLSearchParams({ q, page: String(page), size: String(size) });
  appendFilters(params, filters);
  return apiClient.get<FullSearchResult>(`/api/search/full?${params}`);
};

/**
 * Load-more endpoint for a specific tab on the search results page.
 * Only the requested category field is populated in the response.
 * The same optional filters as fullSearch apply (entries category only).
 */
export const searchCategory = async (
  q: string,
  type: string,
  page: number,
  size = 20,
  filters?: SearchFilters
): Promise<FullSearchResult> => {
  const params = new URLSearchParams({ q, type, page: String(page), size: String(size) });
  appendFilters(params, filters);
  return apiClient.get<FullSearchResult>(`/api/search/category?${params}`);
};